import React, { useEffect, useState } from "react";
import { getOrCreateInventory, updateInventory } from "../../core/api/products";
import { ChevronDown, Pocket } from "feather-icons-react/build/IconComponents";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const InventorySection = ({
  vId,
  warehouseId,
  sku = "",
  onlyHistory = false,
}) => {
  const [inventoryDate, setInventoryDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [warehouse, setWarehouse] = useState("");
  const [inventoryOnHand, setInventoryOnHand] = useState(0);
  const [valuePerUnit, setValuePerUnit] = useState(0);
  const [totalPredictedInventoryOnHand, setTotalPredictedInventoryOnHand] =
    useState(0);

  const [history, setHistory] = useState([]);
  const [inventoryOnHandValue, setInventoryOnHandValue] = useState(0);
  const [totalPredictedInventoryValue, setTotalPredictedInventoryValue] =
    useState(0);

  const [inventoryId, setInventoryId] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await getOrCreateInventory(1, vId);
        const inventoryData = response.data;
        setInventoryData(response.data);
        setInventoryId(inventoryData.id);
        setInventoryDate(
          inventoryData.inventory_date || new Date().toISOString().split("T")[0]
        );
        setWarehouse(
          inventoryData.warehouse_detail?.name || "Unknown Warehouse"
        );
        setInventoryOnHand(inventoryData.inventory_on_hand || 0);
        setValuePerUnit(inventoryData.value_per_unit || 0);
        setTotalPredictedInventoryOnHand(
          inventoryData.total_predicted_inventory_on_hand || 0
        );
        setHistory(inventoryData.history || []);
      } catch (error) {
        console.error("Error fetching inventory data:", error);
      }
    };

    fetchInventory();
  }, [vId, warehouseId]);

  // Dynamic calculations
  useEffect(() => {
    const onHandValue =
      (Number(inventoryOnHand) || 0) * (Number(valuePerUnit) || 0);
    setInventoryOnHandValue(onHandValue);

    const predictedValue =
      (Number(totalPredictedInventoryOnHand) || 0) *
      (Number(valuePerUnit) || 0);
    setTotalPredictedInventoryValue(predictedValue);
  }, [inventoryOnHand, valuePerUnit, totalPredictedInventoryOnHand]);

  const handleUpdateInventory = async () => {
    const currentDateTime = new Date().toISOString();

    // Determine previous values from inventoryData
    const previousInventoryOnHand = Number(
      inventoryData?.inventory_on_hand || 0
    );
    const previousValuePerUnit = Number(inventoryData?.value_per_unit || 0);
    const previousTotalPredictedInventoryOnHand = Number(
      inventoryData?.total_predicted_inventory_on_hand || 0
    );

    // Check if any field has changed
    const isInventoryOnHandChanged =
      Number(inventoryOnHand) !== previousInventoryOnHand;
    const isValuePerUnitChanged = Number(valuePerUnit) !== previousValuePerUnit;
    const isTotalPredictedChanged =
      Number(totalPredictedInventoryOnHand) !==
      previousTotalPredictedInventoryOnHand;

    // Proceed with update only if changes are detected
    if (
      isInventoryOnHandChanged ||
      isValuePerUnitChanged ||
      isTotalPredictedChanged
    ) {
      const difference = inventoryOnHand - previousInventoryOnHand;

      const snapshot = {
        inventory_date: currentDateTime,
        inventory_on_hand: inventoryOnHand,
        difference: difference,
      };

      try {
        // Send updated data to the backend
        await updateInventory(inventoryId, {
          product_variation: vId,
          warehouse: warehouseId,
          inventory_date: inventoryDate,
          inventory_on_hand: Number(inventoryOnHand) || 0,
          value_per_unit: Number(valuePerUnit) || 0,
          total_predicted_inventory_on_hand:
            Number(totalPredictedInventoryOnHand) || 0,
        });

        // Update history and success message
        setHistory((prevHistory) => [snapshot, ...prevHistory]);
        Swal.fire("Success", "Inventory updated successfully", "success");
      } catch (error) {
        console.error("Error updating inventory:", error);
        Swal.fire(
          "Error",
          "Failed to update inventory. Please try again.",
          "error"
        );
      }
    } else {
      // Show a message if no changes were detected
      Swal.fire(
        "No Change",
        "No updates were made as there are no changes.",
        "info"
      );
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-GB", { timeZone: "UTC" });
  };
  const HistoryTable = () => {
    return (
      <div className="card mt-3">
        <div className="card-header">
          <h6 className="mb-0">Stock History</h6>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover" style={{ backgroundColor: '#fff' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ 
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#212529',
                    padding: '12px 16px',
                    borderBottom: '2px solid #dee2e6'
                  }}>Inventory Date</th>
                  <th style={{ 
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#212529',
                    padding: '12px 16px',
                    borderBottom: '2px solid #dee2e6'
                  }}>Inventory on Hand</th>
                  <th style={{ 
                    fontWeight: '600',
                    fontSize: '14px',
                    color: '#212529',
                    padding: '12px 16px',
                    borderBottom: '2px solid #dee2e6'
                  }}>Difference</th>
                </tr>
              </thead>
            <tbody>
              {history.map((entry, index) => (
                <tr key={index}>
                  <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{formatDateTime(entry.inventory_date)}</td>
                  <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{entry.inventory_on_hand}</td>
                  <td
                    style={{ padding: '12px 16px', verticalAlign: 'middle' }}
                    className={
                      entry.difference > 0
                        ? "text-success"
                        : entry.difference < 0
                        ? "text-danger"
                        : ""
                    }
                  >
                    {entry.difference > 0
                      ? `+${entry.difference}`
                      : entry.difference}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="card" hidden={onlyHistory}>
      <div className="card-body add-product pb-0">
        <div
          className="accordion-card-one accordion"
          id="accordionInventory"
        >
          <div className="accordion-item">
            <div className="accordion-header" id="headingInventory">
              <div
                className="accordion-button"
                data-bs-toggle="collapse"
                data-bs-target="#collapseInventory"
                aria-controls="collapseInventory"
              >
                <div className="addproduct-icon">
                  <h5>
                    <Pocket className="add-info" />
                    <span>Stock Management: {sku}</span>
                  </h5>
                  <Link to="#">
                    <ChevronDown className="chevron-down-add" />
                  </Link>
                </div>
              </div>
            </div>

            <div
              id="collapseInventory"
              className="accordion-collapse collapse"
              aria-labelledby="headingInventory"
              data-bs-parent="#accordionInventory"
            >
              <div className="accordion-body">
                <div className="row">
                  {/* Warehouse */}
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">Warehouse</label>
                      <input
                        type="text"
                        className="form-control"
                        value={warehouse}
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Inventory Date */}
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">Inventory Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={inventoryDate}
                        onChange={(e) => setInventoryDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  {/* Inventory on Hand */}
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">Inventory on Hand</label>
                      <input
                        type="number"
                        className="form-control"
                        value={inventoryOnHand}
                        onChange={(e) =>
                          setInventoryOnHand(Number(e.target.value))
                        }
                      />
                    </div>
                  </div>

                  {/* Value per Unit */}
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">Value per Unit</label>
                      <input
                        type="number"
                        className="form-control"
                        value={valuePerUnit}
                        onChange={(e) =>
                          setValuePerUnit(Number(e.target.value))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  {/* Inventory on Hand Value */}
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">
                        Inventory on Hand Value
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={`$${inventoryOnHandValue.toFixed(3)}`}
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Total Predicted Inventory on Hand */}
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">
                        Total Predicted Inventory on Hand
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={totalPredictedInventoryOnHand}
                        onChange={(e) =>
                          setTotalPredictedInventoryOnHand(
                            Number(e.target.value)
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="col-lg-6 col-sm-6 col-12">
                    <div className="mb-3 add-product">
                      <label className="form-label">
                        Total Predicted Inventory on Hand Value
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={totalPredictedInventoryValue.toFixed(3)}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpdateInventory}
                  >
                    Update Inventory
                  </button>
                </div>
              </div>
              <HistoryTable />
              <br />
            </div>
          </div>
        </div>
      </div>
      {onlyHistory && <HistoryTable />}
    </div>
  );
};
export default InventorySection;
